import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Pizza, LogOut, Menu, X } from "lucide-react";

const linkCls = ({ isActive }) =>
  `text-sm font-semibold transition-colors ${isActive ? "text-[#D94A3D]" : "text-[#17324D]/80 hover:text-[#D94A3D]"}`;
const mobileLinkCls = ({ isActive }) =>
  `block px-4 py-3 rounded-xl text-base font-semibold ${isActive ? "bg-[#D94A3D]/10 text-[#D94A3D]" : "text-[#17324D] hover:bg-[#17324D]/5"}`;
const CTA = "inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#D94A3D] text-white text-sm font-bold hover:bg-[#C33E32] transition-colors";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashPath = user?.role === "owner" ? "/owner" : user?.role === "admin" ? "/admin" : "/dashboard";
  const links = [
    { to: "/clubs", label: "Browse Clubs", id: "nav-clubs-link" },
    { to: "/blog", label: "Blog", id: "nav-blog-link" },
    ...(user?.role === "customer" ? [{ to: "/card", label: "My Card", id: "nav-card-link" }] : []),
    ...(user ? [{ to: dashPath, label: "Dashboard", id: "nav-dashboard-link" }] : []),
  ];
  const doLogout = () => { setOpen(false); logout(); navigate("/"); };

  return (
    <header data-testid="header-nav" className="sticky top-0 z-50 bg-[#FFF4E6]/90 backdrop-blur-md border-b border-[#17324D]/10 text-[#17324D]">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-home-link" onClick={() => setOpen(false)} className="flex items-center gap-2 shrink-0">
          <span className="bg-[#D94A3D] text-white p-1.5 rounded-lg"><Pizza size={18} strokeWidth={2.5} /></span>
          <span className="font-heading font-extrabold text-lg tracking-tight">Regulars Club</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => <NavLink key={l.id} to={l.to} data-testid={l.id} className={linkCls}>{l.label}</NavLink>)}
          {user ? (
            <>
              <span className="text-sm text-[#17324D]/60">{user.name}</span>
              <button data-testid="nav-logout-btn" onClick={doLogout} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#17324D]/80 hover:text-[#D94A3D] transition-colors">
                <LogOut size={15} /> Logout
              </button>
            </>
          ) : (
            <>
              <NavLink to="/login" data-testid="nav-login-link" className={linkCls}>Login</NavLink>
              <Link to="/register" data-testid="nav-register-link" className={CTA}>Get Started</Link>
            </>
          )}
        </nav>

        <div className="flex md:hidden items-center gap-2">
          {!user && <Link to="/register" data-testid="nav-register-link-mobile" className={`${CTA} px-4 py-2`}>Get Started</Link>}
          <button data-testid="nav-menu-toggle" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
            className="p-2 rounded-lg hover:bg-[#17324D]/5 transition-colors">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-[#17324D]/10 bg-[#FFF4E6] px-4 py-3 space-y-1">
          {links.map((l) => <NavLink key={l.id} to={l.to} data-testid={`${l.id}-mobile`} onClick={() => setOpen(false)} className={mobileLinkCls}>{l.label}</NavLink>)}
          {user ? (
            <button data-testid="nav-logout-btn-mobile" onClick={doLogout} className="w-full text-left flex items-center gap-2 px-4 py-3 rounded-xl text-base font-semibold text-[#17324D] hover:bg-[#17324D]/5">
              <LogOut size={17} /> Logout <span className="ml-auto text-sm font-normal text-[#17324D]/60">{user.name}</span>
            </button>
          ) : (
            <NavLink to="/login" data-testid="nav-login-link-mobile" onClick={() => setOpen(false)} className={mobileLinkCls}>Login</NavLink>
          )}
        </div>
      )}
    </header>
  );
}
